import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Modal,
  Layout,
  Page,
  Badge,
  Button,
  Link,
  TextStyle,
  DisplayText,
  SkeletonDisplayText,
} from "@shopify/polaris";
import { ShopContext } from "../../App";
import currencySymbol from "currency-symbol-map";
import _ from "lodash";
import { DateTime } from "luxon";
import axios from "../../axios";
import { useHistory } from "react-router-dom";
import Br from "../../components/Br";
import CheckoutAutomations from "../../components/CheckoutAutomations";
import Dashboard from "../dashboard/Dashboard";
import { loadAutomations } from "../../utils";

function CheckoutsPage() {
  const config = useContext(ShopContext);
  const [modalActive, setModalActive] = useState(false);
  const handleModalOpen = useCallback(() => setModalActive(true), []);
  const handleModalClose = useCallback(() => {
    setModalActive(false);
  }, []);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [checkouts, setCheckouts] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [shop, setShop] = useState({});
  const [dashboard, setDashboard] = useState({});
  const [pageDetails, setPageDetails] = useState({
    totalPages: 0,
    previousPage: false,
    nextPage: false,
    totalItems: 0,
  });
  const router = useHistory();
  /**
   * get the skeleton content for page data table
   * @returns {[]}
   */
  const getTableSkeletonContent = () => {
    let skeleton = [];
    for (let i = 0; i < 4; i++) {
      skeleton.push([
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
        <SkeletonBodyText lines={1} />,
      ]);
    }
    return skeleton;
  };
  let skeleton = getTableSkeletonContent();
  /**
   * Column content types of data table
   * @returns {string[]}
   */
  const columnContentTypes = () => {
    return ["text", "text", "text", "text", "text", "text", "text"];
  };
  /**
   * Data table headings
   * @returns {string[]}
   */
  const tableHeadings = () => {
    return [
      "Checkout",
      "Date",
      "Customer",
      "Amount",
      "Status",
      "Automation status",
      "Manual message",
    ];
  };
  /**
   * get the checkout status from the date given
   * @param recoveredAt
   * @returns {{recovered: boolean, variant: string, label: string, abandoned: boolean}}
   */
  const _getCheckoutStatus = (recoveredAt) => {
    let status = {
      label: "Abandoned",
      variant: "critical",
      recovered: false,
      abandoned: false,
    };
    if (recoveredAt !== null) {
      status.label = "Recovered";
      status.variant = "success";
    }
    return status;
  };
  /**
   * get the message status
   * @param messageStatus
   * @returns {{progress: string, label: string, status: string}}
   */
  const _getMessageStatus = (messageStatus) => {
    let status = {
      label: "Message not sent",
      progress: "incomplete",
      status: "warning",
    };
    if (parseInt(messageStatus) === 1) {
      status.label = "Message sent";
      status.progress = "complete";
      status.status = "success";
    }
    return status;
  };
  /**
   * process the response to generate the table columns data
   * @param checkouts
   * @param shop
   * @returns {[]}
   */
  const _processResponseCheckouts = (checkouts, shop) => {
    let processedCheckouts = [];
    if (checkouts.length > 0) {
      checkouts.forEach((checkout) => {
        let cartStatus = _getCheckoutStatus(checkout.recovered_at);
        let newCheckout = [];
        newCheckout.push(
          <Link
            url={`https://${config.shopOrigin}/admin/checkouts/${checkout.checkout_id}`}
            external={true}
          >
            #{checkout.checkout_id}
          </Link>
        );
        newCheckout.push(
          DateTime.fromMillis(parseInt(checkout.created_at)).toLocaleString(
            DateTime.DATETIME_SHORT
          )
        );
        newCheckout.push(
          <>
            {checkout.phone_number}
            <Br />
            {checkout.firstname} {checkout.lastname}
            <Br />
            Opted-in :&nbsp;
            {checkout.buyer_accepts_marketing === 1 ? (
              <Badge status="success"> Yes </Badge>
            ) : (
              <Badge status="critical"> No </Badge>
            )}
          </>
        );
        newCheckout.push(
          <>
            {currencySymbol(checkout.presentment_currency)}
            {parseFloat(checkout.total_amount).toFixed(2)}
          </>
        );
        newCheckout.push(
          <div>
            <Badge status={cartStatus.variant}>{cartStatus.label}</Badge>
            <Br />
          </div>
        );
        let automationStatus = _.get(checkout, "automation_status", null);
        let automationData = null;
        if (shop.has_active_subscription === 1) {
          switch (automationStatus) {
            case "queued":
              automationStatus = "Scheduled";
              break;
            case "ended":
              automationStatus = "Completed";
              break;
            case "started":
              automationStatus = "Started";
              break;
            default:
              automationStatus = "No automation";
          }
          automationData = (
            <>
              {automationStatus}
              <Br height={10} />
              <CheckoutAutomations checkoutId={checkout.checkout_id} />
            </>
          );
        } else {
          automationData = (
            <Button
              secondary
              onClick={() => {
                router.push(`/pricing`);
              }}
            >
              Upgrade
            </Button>
          );
        }
        newCheckout.push(automationData);
        /*if (checkout.recovered_at === null) {*/
        newCheckout.push(
          <Button
            primary
            onClick={() => {
              router.push(`/checkouts/send-message/${checkout.id}/pre_order`);
            }}
          >
            Send message
          </Button>
        );
        /*} else {
          newCheckout.push(<Button disabled>Recovered</Button>);
        }*/
        processedCheckouts.push(newCheckout);
      });
    }
    return processedCheckouts;
  };
  /**
   * Load the checkouts from API
   * @type {function(): void}
   */
  const loadCheckouts = () => {
    setLoading(true);
    axios
      .get(`/checkouts?page=${page}`, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        let checkouts = _processResponseCheckouts(
          response.data.data,
          response.shop
        );
        setCheckouts(checkouts);
        setPageDetails({
          totalPages: response.data.totalPages,
          previousPage: response.data.previousPage,
          nextPage: response.data.nextPage,
          totalItems: response.data.totalItems,
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  /**
   * import the Checkouts from the shopify using API
   */
  const importCheckouts = () => {
    setImporting(true);
    axios
      .get(`/checkouts/import`, {
        headers: {
          shopOrigin: config.shopOrigin,
          xShopHost: config.host,
        },
      })
      .then((response) => {
        let checkouts = _processResponseCheckouts(response.data.data);
        setCheckouts(checkouts);
        setPageDetails({
          totalPages: response.data.totalPages,
          previousPage: response.data.previousPage,
          nextPage: response.data.nextPage,
          totalItems: response.data.totalItems,
        });
        setImporting(false);
        handleModalClose();
      })
      .catch(() => {
        setImporting(false);
        handleModalClose();
      });
  };

  /**
   * Load checkouts when page loads
   * or load when the particular state changed
   */
  useEffect(() => {
    loadAutomations(
      setShop,
      setLoading,
      setDashboard,
      setAutomations,
      config.shopOrigin,
      config.host
    );
    loadCheckouts();
    // eslint-disable-next-line
  }, [page, config]);

  // Stats
  let deliveredPreOrderPaymentMessages = _.get(
    dashboard,
    "delivered_pre_order_payment_messages",
    0
  );
  let openedPreOrderPaymentMessages = _.get(
    dashboard,
    "opened_pre_order_payment_messages",
    0
  );

  // Percentage Stats
  let preOrderSentMessages = _.get(dashboard, "pre_order_sent_messages", 0);

  let preOrderPaymentDeliveryPercentage =
    preOrderSentMessages > 0
      ? (deliveredPreOrderPaymentMessages * 100) / preOrderSentMessages
      : 0;
  let preOrderPaymentOpenPercentage =
    deliveredPreOrderPaymentMessages > 0
      ? (openedPreOrderPaymentMessages * 100) / deliveredPreOrderPaymentMessages
      : 0;

  return (
    <>
      <Page
        fullWidth
        title="Abandoned Checkouts"
        primaryAction={{
          content: "Refresh",
          accessibilityLabel: "Refresh",
          onAction: () => {
            loadCheckouts();
          },
          loading: loading,
        }}
        secondaryActions={[
          {
            content: "Import",
            accessibilityLabel: "Import",
            onAction: handleModalOpen,
            loading: importing,
          },
        ]}
        pagination={{
          hasPrevious: pageDetails.previousPage,
          hasNext: pageDetails.nextPage,
          onNext: () => {
            setPage(page + 1);
          },
          onPrevious: () => {
            setPage(page - 1);
          },
        }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div style={{ width: "50%", padding: "20px 20px 0px 0px" }}>
            <Card title="" sectioned>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-around",
                  minHeight: "265px",
                }}
              >
                <div style={{ width: "50%" }}>
                  <Br height={25} />
                  <span>
                    <img
                      style={{
                        display: "block",
                        marginLeft: "auto",
                        marginRight: "auto",
                      }}
                      alt=""
                      width="50%"
                      height="50%"
                      src="../../Assets/illustrations/delivery-rate.svg"
                    />
                  </span>
                  <Br height={25} />
                  <div
                    style={{ display: "flex", justifyContent: "space-around" }}
                  >
                    <div>
                      <TextStyle variation="subdued">Delivery Rate</TextStyle>
                      <br />
                      <TextStyle variation="small">
                        {loading && (
                          <>
                            <Br height={5} />
                            <SkeletonBodyText lines={1} />
                          </>
                        )}
                        {!loading && (
                          <span style={{ color: "#008060" }}>
                            {deliveredPreOrderPaymentMessages} deliveries
                          </span>
                        )}
                      </TextStyle>
                    </div>
                    <div>
                      <DisplayText size="medium">
                        {loading && <SkeletonDisplayText size="medium" />}
                        {!loading && (
                          <>{Math.ceil(preOrderPaymentDeliveryPercentage)}%</>
                        )}
                      </DisplayText>
                    </div>
                  </div>
                </div>
                <div
                  style={{ borderRight: "1px solid rgba(124, 116, 116, 0.2)" }}
                ></div>
                <div style={{ width: "50%" }}>
                  <span style={{ float: "right" }}>
                    <Link
                      removeUnderline
                      onClick={() => {
                        router.push("/message-history?type=pre_order");
                      }}
                    >
                      <span style={{ color: "#008060" }}>
                        View message history
                      </span>
                    </Link>
                  </span>
                  <Br height={25} />
                  <img
                    style={{
                      display: "block",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                    alt=""
                    width="50%"
                    height="50%"
                    src="../../Assets/illustrations/read-rate.svg"
                  />
                  <Br height={25} />
                  <div
                    style={{ display: "flex", justifyContent: "space-around" }}
                  >
                    <span>
                      <TextStyle variation="subdued">Read Rate</TextStyle>
                      <br />
                      <TextStyle variation="small">
                        {loading && (
                          <>
                            <Br height={5} />
                            <SkeletonBodyText lines={1} />
                          </>
                        )}
                        {!loading && (
                          <span style={{ color: "#008060" }}>
                            {openedPreOrderPaymentMessages} opens
                          </span>
                        )}
                      </TextStyle>
                    </span>
                    <span>
                      <DisplayText size="medium">
                        {loading && <SkeletonBodyText lines={1} />}
                        {!loading && (
                          <>{Math.ceil(preOrderPaymentOpenPercentage)}%</>
                        )}
                      </DisplayText>
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <Dashboard />
        </div>
        <Br height={5} />
        <Layout>
          <Layout.Section>
            <Card>
              <DataTable
                columnContentTypes={columnContentTypes()}
                headings={tableHeadings()}
                rows={loading ? skeleton : checkouts}
                footerContent={
                  loading
                    ? `Loading checkouts! please wait...`
                    : `Showing ${checkouts.length} of ${pageDetails.totalItems} results`
                }
              />
            </Card>
          </Layout.Section>
        </Layout>
        <Modal
          instant
          open={modalActive}
          onClose={handleModalClose}
          title="Import abandoned checkouts"
          primaryAction={{
            content: "Continue to import",
            onAction: importCheckouts,
            loading: importing,
            disabled: importing,
          }}
          secondaryActions={[
            {
              content: "Cancel",
              onAction: handleModalClose,
            },
          ]}
        >
          <Modal.Section>
            <p>
              This action will import the abandoned checkouts with phone numbers
              of last 30 days.
            </p>
          </Modal.Section>
        </Modal>
      </Page>
    </>
  );
}

export default CheckoutsPage;
